import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Col,
  Container,
  Form,
  Image,
  Navbar,
  ProgressBar,
  Row,
  Table,
} from "react-bootstrap";
import { Helmet } from "react-helmet";
import { AiFillCar, AiOutlineUsergroupAdd } from "react-icons/ai";
import { BsClipboardCheck, BsWrenchAdjustable } from "react-icons/bs";
import { MdOutlineReceiptLong, MdPointOfSale } from "react-icons/md";
import { TbReportMoney, TbReportSearch } from "react-icons/tb";
import Loader from "react-js-loader";
import Moment from "react-moment";
import { NumericFormat } from "react-number-format";
import { Link, useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Header from "../Layout/Header";
import SidebarMenu from "../Layout/SidebarMenu";
import BrandLogo from "../logo.png";
import { useAppConfig } from "../Utils/AppConfigContext";
import { BadgeStatus, getEndpointUrl, getTimestamp } from "../Utils/Common";
import BoxTotal from "./Account/BoxTotal";
import BoxUserLogin from "./Account/BoxUserLogin";

const MONTH_NAMES = [
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember",
];

const SHORT_MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Mei",
  "Jun",
  "Jul",
  "Agt",
  "Sep",
  "Okt",
  "Nov",
  "Des",
];

const CHART_COLORS = [
  "#42A5F5",
  "#66BB6A",
  "#FFA726",
  "#EF5350",
  "#AB47BC",
  "#26C6DA",
  "#FFEE58",
  "#8D6E63",
  "#78909C",
  "#EC407A",
];

const formatCurrency = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}jt`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}rb`;
  return value;
};

const CustomTooltipRevenue = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip">
        <p className="fw-bold mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p
            key={index}
            style={{ color: entry.color, margin: 0, fontSize: 13 }}
          >
            {entry.name}:{" "}
            <NumericFormat
              value={entry.value}
              displayType={"text"}
              thousandSeparator={true}
              prefix={"Rp"}
            />
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const [isLoading, setLoading] = useState(true);

  const { config, loading, error, getAppName } = useAppConfig();

  const [userLogin] = useState(
    JSON.parse(localStorage.getItem("knowLogin")) || [],
  );

  const [accesToken] = useState(
    JSON.parse(localStorage.getItem("credentials")) || [],
  );

  const [sidebarVisible, setSidebarVisible] = useState(false);

  const [data, setData] = useState([]);

  const navigate = useNavigate();

  // Filter month/year — default bulan & tahun saat ini
  const currentDate = new Date();
  const [filterMonth, setFilterMonth] = useState(currentDate.getMonth() + 1);
  const [filterYear, setFilterYear] = useState(currentDate.getFullYear());

  const fetchData = useCallback(async () => {
    setLoading(true);
    fetch(
      getEndpointUrl() +
        `/report/dashboard?user_id=${userLogin.id}&month=${filterMonth}&year=${filterYear}&v=${getTimestamp()}`,
      {
        headers: new Headers({
          Authorization: `Bearer ${accesToken}`,
        }),
      },
    )
      .then((response) => response.json())
      .then((json) => {
        if (json.status === "ok") {
          setData(json.results);
        }

        setLoading(false);
      });
  }, [filterMonth, filterYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Transform chartTopBrand for BarChart
  const topBrandData = (data.chartTopBrand || []).map((item) => ({
    name: `${item.brandName.trim()} ${item.type.trim()}`,
    total: parseInt(item.total) || 0,
  }));

  // Transform chartRevenue for ComposedChart
  const revenueData = (data.chartRevenue || []).map((item) => {
    const monthIndex = parseInt(item.month.split("-")[1]) - 1;
    return {
      name: SHORT_MONTH_NAMES[monthIndex] || item.month,
      paid: item.paid || 0,
      unpaid: item.unpaid || 0,
    };
  });

  // Transform chartOdometer for horizontal bars
  const odometerHighest = (data.chartOdometer?.highest || []).map((item) => ({
    name: `${item.brandName.trim()} ${item.type.trim()}  (${item.builtYear})`,
    odometer: parseInt(item.odometer) || 0,
    odometerLabel: item.odometer,
    usiaPakai: item.usiaPakai || "-",
    kmPerTahun: item.kmPerTahun || "-",
    level: item.level || "-",
    score: item.score || "-",
  }));

  const odometerLowest = (data.chartOdometer?.lowest || []).map((item) => ({
    name: `${item.brandName.trim()} ${item.type.trim()}  (${item.builtYear})`,
    odometer: parseInt(item.odometer) || 0,
    odometerLabel: item.odometer,
    usiaPakai: item.usiaPakai || "-",
    kmPerTahun: item.kmPerTahun || "-",
    level: item.level || "-",
    score: item.score || "-",
  }));

  // Get max odometer for percentage bars
  const maxOdometerHigh = Math.max(
    ...odometerHighest.map((d) => d.odometer),
    1,
  );
  const maxOdometerLow = Math.max(...odometerLowest.map((d) => d.odometer), 1);

  // Generate year options (current year ± 3)
  const yearOptions = [];
  for (
    let y = currentDate.getFullYear() - 3;
    y <= currentDate.getFullYear() + 1;
    y++
  ) {
    yearOptions.push(y);
  }

  return (
    <>
      <Helmet>
        <title>Dasboard - {getAppName()}</title>
      </Helmet>
      <div className="d-block d-sm-none">
        <Navbar
          expand="lg"
          className="mainNavbar border-bottom mb-3"
          sticky="top"
        >
          <Container>
            <Navbar.Brand href="/">
              <Image
                src={BrandLogo}
                alt="Brand Logo"
                style={{ height: "40px" }}
              />
              <span className="ms-2 fw-bold fs-12 px-2 py-1 border rounded">{`v${process.env.REACT_APP_VERSION}`}</span>
            </Navbar.Brand>
            <Navbar.Toggle
              className="d-block d-sm-none"
              onClick={(e) => setSidebarVisible(true)}
            />
          </Container>
        </Navbar>
      </div>
      <div className="d-none d-sm-inline">
        <Header />
      </div>
      <Container className="dashboard mt-4">
        {isLoading ? (
          <Row>
            <Col className="d-flex justify-content-center mt-5 pt-5 mb-3">
              <Loader type="bubble-scale" bgColor={"#42A5F5"} size={100} />
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md={12}>
              {/* Title & Filter */}
              <Row className="mb-3 align-items-center">
                <Col md={6}>
                  <strong>Dashboard</strong>
                  <p className="text-muted mb-0">
                    <small>Dashboard akan terakumulasi setiap saat.</small>
                  </p>
                </Col>
                <Col md={6}>
                  <div className="d-flex gap-2 justify-content-md-end align-items-center mt-3">
                    <Form.Select
                      size="sm"
                      className="dashboard-filter-select"
                      value={filterMonth}
                      onChange={(e) =>
                        setFilterMonth(parseInt(e.target.value) || "")
                      }
                    >
                      <option value={"all"}>Semua data</option>
                      {MONTH_NAMES.map((name, idx) => (
                        <option value={idx + 1}>{name}</option>
                      ))}
                    </Form.Select>
                    <Form.Select
                      size="sm"
                      className="dashboard-filter-select"
                      value={filterYear}
                      onChange={(e) => setFilterYear(parseInt(e.target.value))}
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </Col>
              </Row>

              {/* Cards Section — Admin */}
              {userLogin.role == "admin" ? (
                <Row className="mb-3">
                  <Col sm={6} md={4} lg={3}>
                    <BoxTotal
                      title="Total Target Inspeksi"
                      value={data.cardTotalInspeksi}
                      bgColor="danger"
                      icon={<AiFillCar />}
                    />
                  </Col>
                  <Col sm={6} md={4} lg={3}>
                    <BoxTotal
                      title="Belum Dibayar"
                      value={data.cardBelumDibayar}
                      bgColor="warning"
                      prefix="Rp"
                      icon={<MdOutlineReceiptLong />}
                    />
                  </Col>
                  <Col sm={6} md={4} lg={3}>
                    <BoxTotal
                      title="Tagihan Dibayarkan"
                      value={data.totalRevenue}
                      bgColor="success"
                      prefix="Rp"
                      icon={<TbReportMoney />}
                    />
                  </Col>
                  <Col sm={6} md={4} lg={3}>
                    <BoxTotal
                      title="Penawaran"
                      value={data.cardPenawaran}
                      bgColor="info"
                      icon={<MdPointOfSale />}
                    />
                  </Col>
                  <Col sm={6} md={4} lg={3}>
                    <BoxTotal
                      title="Total Pelanggan"
                      value={data.cardTotalPelanggan}
                      bgColor="success"
                      icon={<AiOutlineUsergroupAdd />}
                    />
                  </Col>
                </Row>
              ) : (
                <Row className="mb-3">
                  <Col sm={6} md={4} lg={3}>
                    <BoxTotal
                      title="Total Inspecti Selesai"
                      value={data.totalSelesaiPekerjaan}
                      bgColor="success"
                      icon={<BsClipboardCheck />}
                    />
                  </Col>
                  <Col sm={6} md={4} lg={3}>
                    <BoxTotal
                      title="Belum Dikerjakan"
                      value={data.prPekerjaan}
                      bgColor="warning"
                      icon={<BsWrenchAdjustable />}
                    />
                  </Col>
                  <Col sm={6} md={4} lg={3}>
                    <BoxTotal
                      title="Sedang Dikerjakan"
                      value={data.totalOnprogressPekerjaan}
                      bgColor="info"
                      icon={<TbReportSearch />}
                    />
                  </Col>
                  <Col sm={6} md={4} lg={3}>
                    <BoxTotal
                      title="Total Inspeksi"
                      value={data.totalMyInspeksi}
                      bgColor="danger"
                      icon={<AiFillCar />}
                    />
                  </Col>
                </Row>
              )}

              {/* Chart Top Brand + Tren Mobil Table */}
              {userLogin.role == "admin" && (
                <Row className="mb-4">
                  <Col md={5}>
                    <div className="dashboard-section">
                      <strong>Top Brand Inspeksi</strong>
                      <p className="text-muted">
                        <small>10 brand mobil terbanyak diinspeksi.</small>
                      </p>
                      {topBrandData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart
                            data={topBrandData}
                            margin={{ top: 5, right: 5, left: 0, bottom: 0 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#f0f0f0"
                            />
                            <XAxis
                              dataKey="name"
                              angle={-35}
                              textAnchor="end"
                              interval={0}
                              tick={{ fontSize: 11 }}
                              height={80}
                            />
                            <YAxis
                              allowDecimals={false}
                              tick={{ fontSize: 12 }}
                            />
                            <Tooltip
                              contentStyle={{
                                background: "#fff",
                                border: "1px solid #e0e0e0",
                                borderRadius: 8,
                                fontSize: 13,
                              }}
                            />
                            <Bar
                              dataKey="total"
                              name="Total Unit"
                              radius={[4, 4, 0, 0]}
                            >
                              {topBrandData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    CHART_COLORS[index % CHART_COLORS.length]
                                  }
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Alert variant="warning" className="text-xs">
                          Tidak ada data top brand.
                        </Alert>
                      )}
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="dashboard-section">
                      <strong>Tren Mobil</strong>
                      <p className="text-muted">
                        <small>Dashboard akan terakumulasi setiap saat.</small>
                      </p>
                      <div className="tren-mobil-scroll">
                        <Table striped hover size="sm">
                          <thead>
                            <tr className="bg-gray">
                              <td className="fw-medium text-left" width={10}>
                                #
                              </td>
                              <td className="fw-medium text-left" width={130}>
                                Mobil
                              </td>
                              <td className="fw-medium text-left" width={50}>
                                Total Inspeksi
                              </td>
                            </tr>
                          </thead>
                          <tbody>
                            {data.trenInspeksi?.length > 0 &&
                              data.trenInspeksi.map((val, key) => (
                                <tr key={`item-${key}`}>
                                  <td>{`${key + 1}.`}</td>
                                  <td>{`${val.brandName} - ${val.type}`}</td>
                                  <td className="fw-medium">
                                    <NumericFormat
                                      value={val.totalUnit}
                                      displayType={"text"}
                                      thousandSeparator={true}
                                      className="bg-transparent p-0"
                                      prefix={""}
                                      renderText={(value, props) => (
                                        <span {...props}>{value} Unit</span>
                                      )}
                                    />
                                  </td>
                                </tr>
                              ))}
                            {(!data.trenInspeksi ||
                              data.trenInspeksi.length === 0) && (
                              <tr>
                                <td
                                  colSpan={3}
                                  className="text-center text-muted"
                                >
                                  Tidak ada data
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </Col>
                  <Col md={3}>
                    <BoxUserLogin />
                  </Col>
                </Row>
              )}

              {/* Revenue Trend Chart + User Login */}
              {userLogin.role == "admin" && (
                <Row className="mb-4">
                  <Col md={6}>
                    <div className="dashboard-section">
                      <strong>Tren Revenue</strong>
                      <p className="text-muted">
                        <small>
                          Pendapatan dari invoice yang telah dibayarkan &amp;
                          yang belum dibayarkan.
                        </small>
                      </p>
                      {revenueData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <ComposedChart
                            data={revenueData}
                            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#f0f0f0"
                            />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                            <YAxis
                              tickFormatter={formatCurrency}
                              tick={{ fontSize: 12 }}
                            />
                            <Tooltip content={<CustomTooltipRevenue />} />
                            <Legend
                              wrapperStyle={{ fontSize: 13, paddingTop: 10 }}
                            />
                            <Bar
                              dataKey="paid"
                              name="Sudah Dibayar"
                              fill="#66BB6A"
                              radius={[4, 4, 0, 0]}
                              barSize={30}
                            />
                            <Line
                              type="monotone"
                              dataKey="unpaid"
                              name="Belum Dibayar"
                              stroke="#EF5350"
                              strokeWidth={2}
                              dot={{ r: 4, fill: "#EF5350" }}
                              activeDot={{ r: 6 }}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      ) : (
                        <Alert variant="warning" className="text-xs">
                          Tidak ada data revenue.
                        </Alert>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="dashboard-section">
                      <strong>Insight Petugas</strong>
                      <p className="text-muted">
                        <small>Petugas yang mengerjakan inspeksi.</small>
                      </p>
                      <Table striped hover size="sm" className="table-petugas">
                        <thead>
                          <tr className="bg-gray">
                            <td className="fw-medium" width={10}>
                              #
                            </td>
                            <td className="fw-medium">Nama Petugas</td>
                            <td className="fw-medium text-center" width={180}>
                              Sedang Dikerjakan
                            </td>
                            <td className="fw-medium text-center" width={180}>
                              Sudah Selesai
                            </td>
                          </tr>
                        </thead>
                        <tbody>
                          {data.tableInsightPetugas?.length > 0 &&
                            data.tableInsightPetugas.map((item, idx) => (
                              <tr key={`petugas-${idx}`}>
                                <td>{idx + 1}.</td>
                                <td>{item.name || "-"}</td>
                                <td className="text-center">
                                  {item.sedangDikerjakan > 0 ? (
                                    <Badge bg="info">
                                      {item.sedangDikerjakan}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted">0</span>
                                  )}
                                </td>
                                <td className="text-center">
                                  {item.sudahSelesai > 0 ? (
                                    <Badge bg="success">
                                      {item.sudahSelesai}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted">0</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          {(!data.tableInsightPetugas ||
                            data.tableInsightPetugas.length === 0) && (
                            <tr>
                              <td
                                colSpan={4}
                                className="text-center text-muted"
                              >
                                Tidak ada data petugas.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Odometer Chart + Table Insight Petugas */}
              {userLogin.role == "admin" && (
                <Row className="mb-4">
                  <Col md={8}>
                    <div className="dashboard-section">
                      <strong>Insight Odometer</strong>
                      <p className="text-muted">
                        <small>
                          Mobil dengan odometer tertinggi &amp; terendah.
                        </small>
                      </p>
                      <Row className="mb-4">
                        <Col md={6}>
                          {/* Odometer Tertinggi */}
                          <div className="mb-3">
                            <span className="fw-medium fs-13">
                              <Badge bg="danger" className="me-1">
                                ▲
                              </Badge>{" "}
                              Odometer Tertinggi
                            </span>
                            <div className="mt-2">
                              {odometerHighest.map((item, idx) => (
                                <div
                                  key={`high-${idx}`}
                                  className="odometer-bar-item"
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span
                                      className="fs-13 text-truncate"
                                      style={{ maxWidth: "60%" }}
                                    >
                                      {item.name}
                                    </span>
                                    <span className="fs-13 fw-medium text-muted">
                                      <NumericFormat
                                        value={item.odometerLabel}
                                        displayType={"text"}
                                        thousandSeparator={true}
                                      />{" "}
                                      km
                                    </span>
                                  </div>
                                  <ProgressBar
                                    now={
                                      item.score
                                    }
                                    variant="danger"
                                    className="odometer-progress"
                                  />
                                  <div className="d-flex justify-content-between align-items-center mt-1">
                                    <span className="fs-12 text-muted">
                                      Usia: {item.usiaPakai} thn
                                    </span>
                                    <span className="fs-12 text-muted">
                                      {item.kmPerTahun} km/thn
                                    </span>
                                    <Badge
                                      bg={
                                        item.level === "Rendah"
                                          ? "success"
                                          : item.level === "Normal"
                                          ? "warning"
                                          : "danger"
                                      }
                                      className="fs-12"
                                    >
                                      {item.level}
                                    </Badge>
                                    <span className="fs-12 fw-medium">
                                      Skor: {item.score}
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {odometerHighest.length === 0 && (
                                <small className="text-muted">
                                  Tidak ada data
                                </small>
                              )}
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          {/* Odometer Terendah */}
                          <div>
                            <span className="fw-medium fs-13">
                              <Badge bg="success" className="me-1">
                                ▼
                              </Badge>{" "}
                              Odometer Terendah
                            </span>
                            <div className="mt-2">
                              {odometerLowest.map((item, idx) => (
                                <div
                                  key={`low-${idx}`}
                                  className="odometer-bar-item"
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span
                                      className="fs-13 text-truncate"
                                      style={{ maxWidth: "60%" }}
                                    >
                                      {item.name}
                                    </span>
                                    <span className="fs-13 fw-medium text-muted">
                                      {item.odometerLabel} km
                                    </span>
                                  </div>
                                  <ProgressBar
                                    now={item.score}
                                    variant="success"
                                    className="odometer-progress"
                                  />
                                  <div className="d-flex justify-content-between align-items-center mt-1">
                                    <span className="fs-12 text-muted">
                                      Usia: {item.usiaPakai} thn
                                    </span>
                                    <span className="fs-12 text-muted">
                                      {item.kmPerTahun} km/thn
                                    </span>
                                    <Badge
                                      bg={
                                        item.level === "Sangat Rendah"
                                          ? "success"
                                          : item.level === "Rendah"
                                          ? "info"
                                          : item.level === "Normal"
                                          ? "warning"
                                          : "danger"
                                      }
                                      className="fs-12"
                                    >
                                      {item.level}
                                    </Badge>
                                    <span className="fs-12 fw-medium">
                                      Skor: {item.score}
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {odometerLowest.length === 0 && (
                                <small className="text-muted">
                                  Tidak ada data
                                </small>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                  <Col md={4}>
                    <strong>Pekerjaan</strong>
                    <p className="text-muted">
                      <small>Data inspeksi belum dikerjakan.</small>
                    </p>
                    {data.inspeksi?.length > 0 &&
                      data.inspeksi.map((item, key) => (
                        <div className="border rounded-3 mb-3" key={key}>
                          <div className="d-flex justify-content-between px-3 py-2">
                            <div>
                              <span className="fs-13">
                                {item.inspectionStatus != "pending" ? (
                                  <>
                                    <Moment format="ddd, DD/MM/YYYY">
                                      {item.inspection_date}
                                    </Moment>
                                    <br />
                                    <Moment
                                      format="HH:mm A"
                                      className="text-muted"
                                    >
                                      {item.inspection_date}
                                    </Moment>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </span>
                            </div>
                            <div className="text-muted">
                              <BadgeStatus status={item.inspectionStatus} />
                            </div>
                          </div>
                          <p className="px-3 fs-13">
                            {" "}
                            <span className="fw-bold">{`${item.brandName} - ${item.type}`}</span>
                            , Tahun{" "}
                            <span className="fw-bold">{item.builtYear}</span>,{" "}
                            <br />
                            Nopol{" "}
                            <span className="fw-bold">{item.policeNumber}</span>
                            <br /> Lokasi {item.location}
                          </p>
                          <div className="d-grid gap-2 p-3 pt-0 pb-3">
                            <Link
                              to={`/inspeksi/${item.id}`}
                              className="btn btn-warning"
                            >
                              Form Inspeksi
                            </Link>
                          </div>
                        </div>
                      ))}
                    {data.inspeksi?.length == 0 && (
                      <Alert variant="warning" className="text-xs">
                        Tidak ada data inspeksi.
                      </Alert>
                    )}
                  </Col>
                </Row>
              )}

              {/* Non-admin: Tren Mobil + User Login (original layout) */}
              {userLogin.role != "admin" && (
                <Row className="mb-4">
                  <Col md={4}>
                    <strong>Tren Mobil</strong>
                    <p className="text-muted">
                      <small>Dashboard akan terakumulasi setiap saat.</small>
                    </p>
                    <Table striped hover size="sm">
                      <thead>
                        <tr className="bg-gray">
                          <td className="fw-medium text-left" width={10}>
                            #
                          </td>
                          <td className="fw-medium text-left" width={130}>
                            Mobil
                          </td>
                          <td className="fw-medium text-left" width={50}>
                            Total Inspeksi
                          </td>
                        </tr>
                      </thead>
                      <tbody>
                        {data.trenInspeksi?.length > 0 &&
                          data.trenInspeksi.map((val, key) => (
                            <tr key={`item-${key}`}>
                              <td>{`${key + 1}.`}</td>
                              <td>{`${val.brandName} - ${val.type}`}</td>
                              <td className="fw-medium">
                                <NumericFormat
                                  value={val.totalUnit}
                                  displayType={"text"}
                                  thousandSeparator={true}
                                  className="bg-transparent p-0"
                                  prefix={""}
                                  renderText={(value, props) => (
                                    <span {...props}>{value} Unit</span>
                                  )}
                                />
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={4}>
                    <BoxUserLogin />
                  </Col>
                </Row>
              )}

              {/* Pekerjaan Section */}
              <Row></Row>
            </Col>
          </Row>
        )}
      </Container>
      <SidebarMenu
        show={sidebarVisible}
        close={(e) => setSidebarVisible(false)}
      />
    </>
  );
};

export default Dashboard;
